import React from "react";
import {
  Shield,
  FileText,
  BookOpen,
  Briefcase,
  Users,
  Mail,
  Scale,
  Linkedin,
  Instagram,
} from "lucide-react";
import { Link } from "react-router-dom";
import LegalDisclaimer from "./LegalDisclaimer";
import Logo from "./Logo";



// Custom X (Twitter) Icon
const XIcon = () => (
  <svg
    viewBox="0 0 24 24"
    className="w-5 h-5"
    fill="currentColor"
    aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

// Custom WhatsApp Icon
const WhatsAppIcon = () => (
  <svg
    viewBox="0 0 24 24"
    className="w-5 h-5"
    fill="currentColor"
    aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const legal = [
    { name: "Términos de Servicio", href: "/terms", icon: FileText },
    { name: "Política de Privacidad", href: "/privacy", icon: Shield },
    { name: "Aviso Legal", href: "/disclaimer", icon: Scale },
    { name: "Política de Cookies", href: "/cookies", icon: FileText },
  ];

  const socialLinks = [
    {
      icon: WhatsAppIcon,
      href: "https://api.whatsapp.com/send?phone=573507679981&text=Hola%2C%20deseo%20informaci%C3%B3n%20sobre%20Mi%20Asistente%20Legal%20IA",
      label: "WhatsApp",
      hoverColor: "hover:bg-green-500 hover:text-white",
    },
    {
      icon: XIcon,
      href: "https://x.com/asistlegalia",
      label: "X",
      hoverColor: "hover:bg-gray-800 hover:text-white",
    },
    {
      icon: () => <Linkedin className="w-5 h-5" />,
      href: "http://linkedin.com/in/asistentelegalai-ai-941734363",
      label: "LinkedIn",
      hoverColor: "hover:bg-[#0A66C2] hover:text-white",
    },
    {
      icon: () => <Instagram className="w-5 h-5" />,
      href: "https://www.instagram.com/miasistentelegalia/",
      label: "Instagram",
      hoverColor: "hover:bg-[#E4405F] hover:text-white",
    },
  ];

  return (
    <footer className="relative bg-gray-900 text-white pt-16 pb-8 overflow-hidden">
      {/* Gradient top border */}
      <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-legal-blue via-legal-gold to-legal-blue" />

      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-legal-blue/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />

      <div className="container mx-auto px-4 relative">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div>
            <div className="mb-4">
              <Logo inverted className="text-lg" />
            </div>

            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              Soluciones legales innovadoras impulsadas por IA para individuos y
              empresas.
            </p>
            <div className="flex space-x-3">
              {socialLinks.map((social, index) => {
                const Icon = social.icon;
                return (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className={`w-10 h-10 rounded-xl bg-gray-800 flex items-center justify-center text-gray-400 transition-all duration-300 ${social.hoverColor} hover:scale-110`}>
                    <Icon />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-gray-300 mb-5">
              Enlaces Rápidos
            </h4>
            <ul className="space-y-3">
              {[
                { name: "Características", href: "/#features" },
                { name: "Precios", href: "/pricing" },
                { name: "Testimonios", href: "/#testimonials" },
                { name: "Contacto", href: "/#contact" },
              ].map((item) => (
                <li key={item.name}>
                  <a
                    href={item.href}
                    className="text-gray-400 hover:text-white text-sm transition-colors hover-underline inline-block py-0.5">
                    {item.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-gray-300 mb-5">
              Servicios
            </h4>
            <ul className="space-y-3">
              {[
                "Análisis Legal",
                "Generación de Documentos",
                "Asesoría Legal",
                "Cumplimiento Normativo",
              ].map((item) => (
                <li key={item}>
                  <a
                    href="#"
                    className="text-gray-400 hover:text-white text-sm transition-colors hover-underline inline-block py-0.5">
                    {item}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-gray-300 mb-5">
              Legal
            </h4>
            <ul className="space-y-3">
              {legal.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className="text-gray-400 hover:text-white text-sm transition-colors flex items-center gap-2 hover-underline inline-flex py-0.5">
                      {item.name}
                      <Icon className="h-3.5 w-3.5 opacity-50" />
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800/50 pt-8 text-center">
          <LegalDisclaimer variant="subtle" className="mb-6 opacity-80" />
          <p className="text-gray-500 text-sm">
            &copy; {currentYear} MiAsistenteLegalIA. Todos los derechos
            reservados.
          </p>
        </div>

      </div>
    </footer>
  );
};

export default Footer;
